export interface ImportedGame {
  id: string;
  pgn: string;
  source: "lichess" | "chesscom";
  opponents: string;
  result: string;
  playedAt: string | null;
}

export function splitPgnGames(text: string): string[] {
  const chunks = text.split(/(?=\[Event )/g);
  return chunks.map((chunk) => chunk.trim()).filter((chunk) => chunk.startsWith("[Event "));
}

export async function fetchLichessGames(username: string, max = 12): Promise<ImportedGame[]> {
  const response = await fetch(
    `https://lichess.org/api/games/user/${encodeURIComponent(username)}?max=${max}`,
    { headers: { Accept: "application/x-chess-pgn" } },
  );
  if (!response.ok) {
    throw new Error(`Lichess returned ${response.status}. Check the username.`);
  }
  const text = await response.text();
  return splitPgnGames(text).map((pgn, index) => {
    const headers = readHeaders(pgn);
    return {
      id: headers.GameId ?? `lichess-${index}`,
      pgn,
      source: "lichess" as const,
      opponents:
        headers.White && headers.Black
          ? `${headers.White} vs ${headers.Black}`
          : (headers.White ?? headers.Black ?? "Unknown"),
      result: headers.Result ?? "*",
      playedAt: headers.UTCDate ?? null,
    };
  });
}

interface ChessComArchive {
  games: Array<{
    url: string;
    pgn: string;
    time_control: string;
    end_time: number;
    rated: boolean;
    white: { username: string };
    black: { username: string };
    eco?: string;
  }>;
}

export async function fetchChessComGames(username: string, max = 12): Promise<ImportedGame[]> {
  const archivesResponse = await fetch(
    `https://api.chess.com/pub/player/${encodeURIComponent(username)}/games/archives`,
  );
  if (!archivesResponse.ok) {
    throw new Error(`Chess.com returned ${archivesResponse.status}. Check the username.`);
  }
  const archives = (await archivesResponse.json()) as { archives: string[] };
  const recent = archives.archives.slice(-2).reverse();

  const collected: ImportedGame[] = [];
  for (const archive of recent) {
    if (collected.length >= max) break;
    const response = await fetch(archive);
    if (!response.ok) continue;
    const payload = (await response.json()) as ChessComArchive;
    for (const game of payload.games) {
      if (!game.pgn || !game.pgn.includes("1.")) continue;
      collected.push({
        id: game.url.split("/").pop() ?? `chesscom-${collected.length}`,
        pgn: game.pgn,
        source: "chesscom",
        opponents: `${game.white.username} vs ${game.black.username}`,
        result: readHeaders(game.pgn).Result ?? "*",
        playedAt: game.end_time ? new Date(game.end_time * 1000).toISOString() : null,
      });
      if (collected.length >= max) break;
    }
  }
  return collected;
}

export function readHeaders(pgn: string): Record<string, string> {
  const headers: Record<string, string> = {};
  const headerBlock = pgn.split(/\n\s*\n/)[0] ?? "";
  for (const line of headerBlock.split("\n")) {
    const match = /^\[([A-Za-z0-9_]+)\s+"?(.*?)"?\]$/.exec(line.trim());
    const key = match?.[1];
    const value = match?.[2];
    if (key !== undefined && value !== undefined) headers[key] = value;
  }
  return headers;
}
